import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  DuplicateDonor,
  DuplicateGroup,
  DuplicateIndividual,
  DuplicateScanResult,
  scanDonorDuplicates,
  scanIndividualDuplicates,
} from '../services/duplicateService';
import { MergeDialog } from '../components/MergeDialog';
import '../styles/ListPages.css';
import '../styles/FormPages.css';

type Tab = 'clients' | 'donors';

export function DuplicatesPage() {
  const [tab, setTab] = useState<Tab>('clients');
  const [minConfidence, setMinConfidence] = useState(70);

  const [clientResult, setClientResult] = useState<DuplicateScanResult<DuplicateIndividual> | null>(
    null
  );
  const [donorResult, setDonorResult] = useState<DuplicateScanResult<DuplicateDonor> | null>(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [activeMerge, setActiveMerge] = useState<
    | { kind: 'client'; group: DuplicateGroup<DuplicateIndividual> }
    | { kind: 'donor'; group: DuplicateGroup<DuplicateDonor> }
    | null
  >(null);

  const runScan = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'clients') {
        const r = await scanIndividualDuplicates(minConfidence);
        setClientResult(r);
      } else {
        const r = await scanDonorDuplicates(minConfidence);
        setDonorResult(r);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Scan failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [tab, minConfidence]);

  // Auto-scan on tab switch (one-time per tab); user re-runs after threshold change.
  useEffect(() => {
    if (tab === 'clients' && !clientResult) {runScan();}
    if (tab === 'donors' && !donorResult) {runScan();}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const onMergeComplete = () => {
    setActiveMerge(null);
    // Re-scan to drop the just-merged group from the list
    runScan();
  };

  const result = tab === 'clients' ? clientResult : donorResult;

  return (
    <div className="list-page">
      <div className="page-header">
        <div>
          <h1>Find Duplicates</h1>
          <p className="page-subtitle">
            Review records that may refer to the same person and merge them. Merges are atomic and
            audit-logged.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <div role="tablist" style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              role="tab"
              aria-selected={tab === 'clients'}
              className={tab === 'clients' ? 'button-primary' : 'button-secondary'}
              onClick={() => setTab('clients')}
            >
              Clients
            </button>
            <button
              role="tab"
              aria-selected={tab === 'donors'}
              className={tab === 'donors' ? 'button-primary' : 'button-secondary'}
              onClick={() => setTab('donors')}
            >
              Donors
            </button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Min confidence:
            <input
              type="range"
              min={0}
              max={95}
              step={5}
              value={minConfidence}
              onChange={(e) => setMinConfidence(parseInt(e.target.value, 10))}
            />
            <strong style={{ minWidth: '2.5em' }}>{minConfidence}%</strong>
          </label>

          <button className="button-primary" onClick={runScan} disabled={loading}>
            {loading ? 'Scanning…' : 'Re-run Scan'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loading && <div className="loading">Scanning for duplicates…</div>}

      {!loading && result && (
        <>
          <div style={{ marginBottom: '1rem' }}>
            <strong>{result.totalGroups}</strong> candidate group
            {result.totalGroups === 1 ? '' : 's'} found
            {result.totalGroups > 0 && ` at ≥ ${minConfidence}% confidence`}
          </div>

          {result.totalGroups === 0 && (
            <div className="card">
              <p>No likely duplicates at this confidence threshold. Try lowering it.</p>
            </div>
          )}

          {tab === 'clients' &&
            clientResult?.groups.map((group, i) => (
              <ClientGroupCard
                key={i}
                group={group}
                onMerge={() => setActiveMerge({ kind: 'client', group })}
              />
            ))}

          {tab === 'donors' &&
            donorResult?.groups.map((group, i) => (
              <DonorGroupCard
                key={i}
                group={group}
                onMerge={() => setActiveMerge({ kind: 'donor', group })}
              />
            ))}
        </>
      )}

      {activeMerge?.kind === 'client' && (
        <MergeDialog
          kind="client"
          group={activeMerge.group}
          onClose={() => setActiveMerge(null)}
          onComplete={onMergeComplete}
        />
      )}
      {activeMerge?.kind === 'donor' && (
        <MergeDialog
          kind="donor"
          group={activeMerge.group}
          onClose={() => setActiveMerge(null)}
          onComplete={onMergeComplete}
        />
      )}
    </div>
  );
}

// =============================================================================
// Group cards
// =============================================================================

function ConfidenceBadge({ value }: { value: number }) {
  let color = '#9D9FA1'; // gray
  if (value >= 90) {color = '#5B2C86';}
  else if (value >= 70) {color = '#FAA31B';}
  return (
    <span
      style={{
        background: color,
        color: 'white',
        padding: '0.15rem 0.5rem',
        borderRadius: '999px',
        fontSize: '0.85rem',
        fontWeight: 600,
      }}
    >
      {value}% confidence
    </span>
  );
}

function ClientGroupCard({
  group,
  onMerge,
}: {
  group: DuplicateGroup<DuplicateIndividual>;
  onMerge: () => void;
}) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
          gap: '1rem',
        }}
      >
        <div>
          <ConfidenceBadge value={group.confidence} />
          <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#666' }}>
            {group.matchedRules.map((r) => r.label).join(' · ')}
          </div>
        </div>
        <button className="button-primary" onClick={onMerge}>
          Review &amp; Merge
        </button>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Client ID</th>
            <th>Name</th>
            <th>DOB</th>
            <th>Phone</th>
            <th>Email</th>
            <th>ZIP</th>
          </tr>
        </thead>
        <tbody>
          {group.members.map((m) => (
            <tr key={m.individualID}>
              <td>
                <Link to={`/clients/${m.individualID}`}>{m.clientID || `#${m.individualID}`}</Link>
              </td>
              <td>
                {m.firstName} {m.lastName}
              </td>
              <td>{m.dateOfBirth?.slice(0, 10) || '—'}</td>
              <td>{m.phoneNumber || '—'}</td>
              <td>{m.emailAddress || '—'}</td>
              <td>{m.zipCode || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DonorGroupCard({
  group,
  onMerge,
}: {
  group: DuplicateGroup<DuplicateDonor>;
  onMerge: () => void;
}) {
  return (
    <div className="card" style={{ marginBottom: '1rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '0.75rem',
          gap: '1rem',
        }}
      >
        <div>
          <ConfidenceBadge value={group.confidence} />
          <div style={{ marginTop: '0.25rem', fontSize: '0.9rem', color: '#666' }}>
            {group.matchedRules.map((r) => r.label).join(' · ')}
          </div>
        </div>
        <button className="button-primary" onClick={onMerge}>
          Review &amp; Merge
        </button>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Donor ID</th>
            <th>Name</th>
            <th>Organization</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {group.members.map((m) => (
            <tr key={m.donorID}>
              <td>#{m.donorID}</td>
              <td>{m.fullName || '—'}</td>
              <td>{m.organizationCompany || '—'}</td>
              <td>{m.emailAddress || '—'}</td>
              <td>{m.phoneNumber || '—'}</td>
              <td>{m.donorType || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
